import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { apiErrorText } from "@/lib/apiError";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import {
  Building2,
  Check,
  X,
  Loader2,
  ShieldAlert,
  Mail,
  UserCheck,
  ArrowLeft,
} from "lucide-react";

/**
 * /admin/claims — moderation queue for university-ownership claims that
 * failed automatic domain-match. Approving inserts the claimant into the
 * uni's `claimed_by` list; rejecting leaves them signed in but denied.
 */
export default function AdminClaims() {
  const { user, login } = useAuth();
  const [state, setState] = useState({ loading: true, forbidden: false, items: [] });
  const [rejecting, setRejecting] = useState(null); // {id, reason}

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/admin/claims/pending");
      setState({ loading: false, forbidden: false, items: data?.items || [] });
    } catch (err) {
      const s = err?.response?.status;
      setState({ loading: false, forbidden: s === 401 || s === 403, items: [] });
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      await api.post(`/admin/claims/${id}/approve`);
      toast.success("Claim approved — owner added");
      load();
    } catch (err) {
      toast.error(apiErrorText(err, "Approval failed"));
    }
  };

  const doReject = async () => {
    if (!rejecting) return;
    try {
      await api.post(`/admin/claims/${rejecting.id}/reject`, {
        reason: rejecting.reason || "",
      });
      toast.success("Claim rejected");
      setRejecting(null);
      load();
    } catch (err) {
      toast.error(apiErrorText(err, "Rejection failed"));
    }
  };

  if (state.loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 text-stone-500 font-mono text-sm" data-testid="admin-claims-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
        Loading claims queue…
      </div>
    );
  }

  if (state.forbidden) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="admin-claims-forbidden">
        <div className="p-8 border border-stone-300 rounded-lg bg-white">
          <ShieldAlert className="h-8 w-8 text-stone-500" />
          <h1 className="mt-3 font-editorial text-2xl">Admin access required</h1>
          <p className="mt-2 text-stone-600 text-sm">
            The claims queue is restricted to editors on the admin allowlist.
          </p>
          {!user && (
            <Button className="mt-4" onClick={login} data-testid="admin-claims-signin-btn">
              Sign in with Google
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="admin-claims-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-wider text-stone-500 hover:text-stone-900" data-testid="admin-claims-back">
        <ArrowLeft className="h-3 w-3" /> Newsroom
      </Link>
      <div className="mt-3 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
            Editorial CMS · Ownership
          </div>
          <h1 className="mt-1 font-editorial text-3xl">University claims queue</h1>
          <p className="mt-2 text-stone-600 text-sm max-w-2xl">
            Pending claims are ownership requests that failed the automatic
            domain check. Approve when you've verified via phone / press
            office that the claimant works there.
          </p>
        </div>
        <div className="text-xs font-mono text-stone-500 uppercase tracking-wider" data-testid="admin-claims-total">
          {state.items.length} pending
        </div>
      </div>

      {state.items.length === 0 ? (
        <div className="mt-10 p-8 border border-dashed border-stone-300 rounded-lg text-center text-stone-500 text-sm" data-testid="admin-claims-empty">
          Nothing to review right now. New claims will appear here when a uni
          staff member signs in with an email that doesn't match the
          university's `official_domains`.
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {state.items.map((c) => (
            <li
              key={c.id}
              data-testid={`admin-claims-row-${c.id}`}
              className="p-5 border border-stone-300 rounded-lg bg-white"
            >
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-stone-900">
                    <Building2 className="h-4 w-4" />
                    <Link
                      to={`/uni/${c.university_id}`}
                      className="font-editorial text-lg hover:underline"
                    >
                      {c.university_name}
                    </Link>
                  </div>
                  <div className="mt-2 text-sm text-stone-700 flex items-center gap-2">
                    <UserCheck className="h-3.5 w-3.5" />
                    <span className="font-mono">{c.name || "(no name)"}</span>
                    {c.role ? <span className="text-stone-500">· {c.role}</span> : null}
                  </div>
                  <div className="mt-1 text-xs text-stone-500 flex items-center gap-2">
                    <Mail className="h-3 w-3" />
                    <span className="font-mono">{c.email}</span>
                  </div>
                  <div className="mt-2 text-[11px] font-underground-sc tracking-wider text-stone-400">
                    Submitted {new Date(c.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex flex-col gap-2 min-w-[130px]">
                  <Button
                    size="sm"
                    onClick={() => approve(c.id)}
                    data-testid={`admin-claims-approve-${c.id}`}
                  >
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRejecting({ id: c.id, reason: "" })}
                    data-testid={`admin-claims-reject-${c.id}`}
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                </div>
              </div>

              {rejecting?.id === c.id && (
                <div className="mt-4 p-4 border border-stone-200 rounded-md bg-stone-50">
                  <div className="text-xs font-semibold text-stone-800">
                    Reason (shown to the claimant)
                  </div>
                  <Textarea
                    className="mt-2"
                    rows={2}
                    value={rejecting.reason}
                    onChange={(e) =>
                      setRejecting({ ...rejecting, reason: e.target.value })
                    }
                    placeholder="e.g. Could not verify with press office"
                    data-testid={`admin-claims-reject-reason-${c.id}`}
                  />
                  <div className="mt-3 flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setRejecting(null)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={doReject} data-testid={`admin-claims-reject-confirm-${c.id}`}>
                      Confirm reject
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
